import {
  ButtonHTMLAttributes,
  CSSProperties,
  ReactNode,
} from 'react';
import { Editor, isTextSelection } from '@tiptap/react';
import { BubbleMenu, BubbleMenuProps } from '@tiptap/react/menus';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/react';

import { Button } from '../../../../common/Atoms/Button';
import { Dropdown } from '../../../../common/Atoms/Dropdown';
import { Badge } from '../../../../common/Atoms/Badge';
import { classNames } from '../../../../common/lib/classNames';

import { ColorPickerEditor, expandSelectionToIncludeMergeTags } from './ColorPickerEditor';
import { EditorIcon } from './EditorIcon';
import { fontFamilies, normalizeFontFamily } from './fontFamilies';
import {
  IDynamicSection,
  SectionTag,
  sectionTagText,
} from './dynamicSections';
import {
  IEmailButtonAttrs,
  buttonShapes,
  buttonSizes,
  insertButton,
  selectedButton,
  setButtonAttrs,
} from './emailButton';
import { insertSenderBlock } from './senderBlock';

type Level = 1 | 2 | 3 | 4 | 5 | 6;

interface MergeTag {
  text: string;
  value: string;
  new?: boolean;
  link?: boolean;
  // Logo and signature, inserted as their own block
  block?: boolean;
  section?: SectionTag;
}

interface MenuBarProps {
  editor?: Editor | null;
  mergeTags?: MergeTag[];
  mode?: `email` | `sms` | `signature` | `note` | `secondaryContent`;
  sections?: IDynamicSection[];
  lockSections?: boolean;
  // Sits above the toolbar, which then has no top corners
  subjectEditor?: Editor | null;
  // Insert field goes into the subject rather than the body
  subjectFocused?: boolean;
  action?: IToolbarAction;
  // Its own segment at the end of the formatting buttons
  extra?: ReactNode;
}

// A button stacked under Insert field, in the same style
export interface IToolbarAction {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

interface MenuOption {
  label: ReactNode;
  onClick: () => void;
  active?: boolean;
  hint?: string;
  isNew?: boolean;
  style?: CSSProperties;
  className?: string;
}

const headingLevels: Level[] = [1, 2, 3, 4, 5, 6];

const fontSizes = [10, 12, 14, 16, 18, 20, 24, 28, 32];

const alignments = [
  { value: `left`, label: `Align left`, icon: `alignLeft` },
  { value: `center`, label: `Align centre`, icon: `alignCenter` },
  { value: `right`, label: `Align right`, icon: `alignRight` },
  { value: `justify`, label: `Justify`, icon: `alignJustify` },
] as const;

// Rendered in place rather than portalled, so a menu opened from the bubble menu
// stays inside it and the bubble menu doesn't hide when the editor loses focus
function ToolbarMenu({ button, buttonClassName, title, options, align = `left`, disabled }: {
  button: ReactNode;
  buttonClassName: string;
  title?: string;
  options: MenuOption[];
  align?: `left` | `right`;
  disabled?: boolean;
}) {
  return (
    <Menu
      as={ `div` }
      className={ `relative` }>
      <MenuButton
        title={ title }
        aria-label={ title }
        disabled={ disabled }
        className={ classNames(buttonClassName, `disabled:cursor-not-allowed disabled:opacity-40`) }>
        { button }
      </MenuButton>
      <MenuItems
        className={ classNames(
          `absolute top-full z-20 mt-1 max-h-72 min-w-44 overflow-auto rounded-lg border border-gray-200 bg-white p-1.5 text-[13px] text-gray-900 shadow-lg focus:outline-none`,
          align === `right` ? `right-0` : `left-0`,
        ) }>
        { options.map((option, i) => (
          <MenuItem key={ i }>
            <button
              type={ `button` }
              onClick={ option.onClick }
              style={ option.style }
              className={ classNames(
                `flex w-full items-center justify-between gap-4 whitespace-nowrap rounded-md px-2 py-1.5 text-left`,
                option.active ? `bg-paidniceGreen-50 text-paidniceGreen-800` : `data-focus:bg-gray-50`,
                option.className,
              ) }>
              <span className={ `flex items-center gap-2` }>
                { option.label }
                { option.isNew ? <Badge
                  color={ `blue` }
                  message={ `New!` } /> : null }
              </span>
              { option.hint ? <span className={ `font-mono text-[11px] text-gray-400` }>{ option.hint }</span> : null }
            </button>
          </MenuItem>
        )) }
      </MenuItems>
    </Menu>
  );
}

function IconButton({ active, className, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }) {
  return (
    <button
      type={ `button` }
      aria-label={ props.title }
      aria-pressed={ active }
      className={ classNames(
        `flex h-7 w-7 items-center justify-center rounded-md`,
        active ? `bg-paidniceGreen-50 text-paidniceGreen-700` : `text-gray-600 hover:bg-gray-100 hover:text-gray-900`,
        className,
      ) }
      { ...props } />
  );
}

function Segment({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={ classNames(`flex items-center gap-0.5 rounded-lg border border-gray-200 bg-white p-0.5`, className) }>
      { children }
    </div>
  );
}

// Also shown with just a cursor in a link, so it can be edited or removed.
// Module level so its identity is stable: BubbleMenu dispatches a transaction
// whenever this prop changes, and the editor re-renders on every transaction
const shouldShowBubbleMenu: BubbleMenuProps[`shouldShow`] = ({ editor, view, state, element }) => {
  const hasFocus = view.hasFocus() || element.contains(document.activeElement);

  return hasFocus && (!!selectedButton(state) || isTextSelection(state.selection) && (!state.selection.empty || editor.isActive(`link`)));
};

// Starts at the selection rather than centring on it, so a wide menu over something near the
// left edge (like a button) runs right instead of under the sidebar. Module level for the same reason as above
const bubbleMenuOptions: BubbleMenuProps[`options`] = { placement: `top-start` };

const sideButton = `flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md px-2 text-[13px] font-medium text-paidniceGreen-800 hover:bg-gray-100`;

const bubbleButton = `flex h-7 w-7 items-center justify-center rounded-[5px] text-paidniceGreen-100 hover:bg-paidniceGreen-700`;
const bubbleTextButton = `flex h-7 items-center px-2 text-xs text-paidniceGreen-200 hover:text-white`;
const bubbleMenuButton = `flex h-7 items-center gap-1 rounded-[5px] px-1.5 text-xs text-paidniceGreen-100 hover:bg-paidniceGreen-700`;

// Shown in the bubble menu in place of the text controls while a button is selected
function ButtonControls({ editor, button, linkTags }: { editor: Editor; button: IEmailButtonAttrs; linkTags: MergeTag[] }) {
  const set = (attrs: Partial<IEmailButtonAttrs>) => setButtonAttrs(editor, attrs);
  const chevron = <EditorIcon name={ `chevronDown` }
    size={ 12 } />;

  return (
    <>
      <input
        aria-label={ `Button text` }
        placeholder={ `Button text` }
        value={ button.label }
        onChange={ e => set({ label: e.target.value }) }
        className={ `h-7 w-32 rounded-[5px] border-0 bg-paidniceGreen-700 px-2 text-xs text-white placeholder:text-paidniceGreen-300 focus:ring-1 focus:ring-paidniceGreen-300` } />
      <ToolbarMenu
        title={ `Button link` }
        buttonClassName={ bubbleMenuButton }
        options={ linkTags.map(t => ({
          label: t.text,
          isNew: t.new,
          active: button.href === `{{{${t.value}}}}`,
          onClick: () => set({ href: `{{{${t.value}}}}` }),
        })) }
        button={ (
          <>
            <EditorIcon name={ `link` }
              size={ 13 } />
            { linkTags.find(t => `{{{${t.value}}}}` === button.href)?.text ?? `Choose link` }
            { chevron }
          </>
        ) } />
      { ([[`backgroundColor`, `Button colour`], [`textColor`, `Text colour`]] as const).map(([key, title]) => (
        <label
          key={ key }
          title={ title }
          className={ classNames(bubbleButton, `cursor-pointer`) }>
          <input
            type={ `color` }
            aria-label={ title }
            value={ button[key] }
            onChange={ e => set({ [key]: e.target.value }) }
            className={ `h-4 w-4 cursor-pointer appearance-none rounded-sm border border-paidniceGreen-300 p-0 [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0` } />
        </label>
      )) }
      <ToolbarMenu
        title={ `Corner style` }
        buttonClassName={ bubbleMenuButton }
        options={ Object.entries(buttonShapes).map(([shape, s]) => ({
          label: s.label,
          active: button.shape === shape,
          onClick: () => set({ shape: shape as IEmailButtonAttrs[`shape`] }),
        })) }
        button={ <>{ buttonShapes[button.shape]?.label }{ chevron }</> } />
      <ToolbarMenu
        title={ `Button size` }
        buttonClassName={ bubbleMenuButton }
        options={ Object.entries(buttonSizes).map(([size, s]) => ({
          label: s.label,
          active: button.size === size,
          onClick: () => set({ size: size as IEmailButtonAttrs[`size`] }),
        })) }
        button={ <>{ buttonSizes[button.size]?.label }{ chevron }</> } />
      <span className={ `mx-1 h-[18px] w-px bg-paidniceGreen-600` } />
      <button
        type={ `button` }
        className={ bubbleTextButton }
        onClick={ () => editor.chain().focus().deleteSelection().run() }>
        { `Remove` }
      </button>
    </>
  );
}

export function MenuBar({ editor, mergeTags = [], mode, sections = [], lockSections, subjectEditor, subjectFocused, action, extra }: MenuBarProps) {
  if (!editor || mode === `note`) return null;

  const inSubject = !!subjectEditor && subjectFocused;

  const insertMergeTag = (tag: string) => {
    (inSubject ? subjectEditor : editor).chain().focus().insertContent(`{{{${tag}}}}`).run();
  };

  // Extending to the whole mark lets a cursor sitting in a link edit or remove all of it
  const setLink = (tag: string) => {
    editor.chain().focus().extendMarkRange(`link`).setLink({ href: `{{{${tag}}}}` }).run();
  };

  const sectionsInBody = new Set<string>();

  editor.state.doc.descendants(node => {
    if (node.type.name === `dynamicSection`) sectionsInBody.add(node.attrs.section);
  });

  // Each section goes in once, never inside another, and a variation can only place one the original has written.
  // The inserted tag is expanded into the section on the next sync, the same as a typed tag
  const sectionOptions: MenuOption[] = editor.isActive(`dynamicSection`) ? [] : sections
    .filter(s => !sectionsInBody.has(s.tag) && (!lockSections || s.html))
    .map(s => ({
      label: `${s.label} section`,
      hint: `Section`,
      onClick: () => editor.chain().focus().insertContent(sectionTagText(s.tag)).run(),
    }));

  // Tags made for the section the cursor is in go first, outlined like the section block
  const toFieldOption = (t: MergeTag): MenuOption => ({
    label: t.text,
    hint: t.section ? `This section` : t.value,
    isNew: t.new,
    className: t.section && `mb-1 border border-dashed border-paidniceGreen-300`,
    onClick: () => (t.block ? insertSenderBlock(editor, t.value) : insertMergeTag(t.value)),
  });
  const sectionTags = mergeTags.filter(t => t.section && editor.isActive(`dynamicSection`, { section: t.section }));

  // Sections, links and blocks make no sense in a subject line
  const fieldOptions: MenuOption[] = inSubject ? mergeTags.filter(t => !t.section && !t.link && !t.block).map(toFieldOption) : [
    ...sectionOptions,
    ...sectionTags.map(toFieldOption),
    ...mergeTags.filter(t => !t.section).map(toFieldOption),
  ];

  // SMS only gets merge tags and the line break toggle
  if (mode === `sms`) {
    return (
      <div
        className={ `
        flex
        flex-wrap
        gap-2
        bg-gray-200
        px-2 py-4
        rounded-t-md
        [&_button]:cursor-pointer
      ` }>
        <div className={ `justify-self-center` }>
          <Dropdown
            theme={ `primary` }
            options={ [mergeTags.map(t => ({
              label: t.text,
              onClick: () => insertMergeTag(t.value),
              badge: t.new && `New!`,
              badgeColor: t.new && `blue`,
            }))] }
            label={ `Merge Tags` }
            position={ `right` }
          />
        </div>
        <Button
          active={ editor.storage.invisibleCharacters?.visibility() }
          size={ `sm` }
          tooltip={ `Show line breaks` }
          onClick={ () => editor.chain().focus().toggleInvisibleCharacters().run() }
        >
          { `¶` }
        </Button>
      </div>
    );
  }

  const isSignature = mode === `signature`;
  const linkTags = isSignature ? [] : mergeTags.filter(t => t.link);
  const button = selectedButton(editor.state);

  const linkOptions: MenuOption[] = linkTags.map(t => ({
    label: t.text,
    isNew: t.new,
    active: editor.isActive(`link`, { href: `{{{${t.value}}}}` }),
    onClick: () => setLink(t.value),
  }));

  const activeHeading = headingLevels.find(level => editor.isActive(`heading`, { level }));

  const textStyleOptions: MenuOption[] = [
    {
      label: `Body text`,
      active: editor.isActive(`paragraph`),
      onClick: () => editor.chain().focus().setParagraph().run(),
    },
    ...headingLevels.map(level => ({
      label: `Heading ${level}`,
      active: activeHeading === level,
      onClick: () => editor.chain().focus().toggleHeading({ level }).run(),
    })),
  ];

  const currentFontFamily = normalizeFontFamily(editor.getAttributes(`textStyle`)?.fontFamily);
  const currentFont = fontFamilies.find(f => f.normalizedValue === currentFontFamily);

  const fontOptions: MenuOption[] = [
    {
      label: `Default font`,
      active: !currentFont,
      onClick: () => editor.chain().focus().setMark(`textStyle`, { fontFamily: null }).removeEmptyTextStyle().run(),
    },
    ...fontFamilies.map(f => ({
      label: f.label,
      style: { fontFamily: f.value },
      active: currentFont === f,
      onClick: () => editor.chain().focus().setMark(`textStyle`, { fontFamily: f.value }).run(),
    })),
  ];

  const currentFontSize = editor.getAttributes(`textStyle`)?.fontSize as string | undefined;

  const fontSizeOptions: MenuOption[] = [
    {
      label: `Default size`,
      active: !currentFontSize,
      onClick: () => editor.chain().focus().command(expandSelectionToIncludeMergeTags).unsetFontSize().run(),
    },
    ...fontSizes.map(size => ({
      label: `${size}`,
      active: currentFontSize === `${size}px`,
      onClick: () => editor.chain().focus().command(expandSelectionToIncludeMergeTags).setFontSize(`${size}px`).run(),
    })),
  ];

  const activeLinkHref = editor.getAttributes(`link`)?.href as string | undefined;
  const activeLinkLabel = activeLinkHref && (linkTags.find(t => `{{{${t.value}}}}` === activeLinkHref)?.text ?? activeLinkHref);

  const textButton = `flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md px-2 text-[13px] text-gray-900 hover:bg-gray-100`;

  return (
    <>
      <div className={ classNames(`flex items-start gap-2 border border-b-0 border-gray-300 bg-gray-50 p-2 [&_button]:cursor-pointer`, !subjectEditor && `rounded-t-md`) }>
        <div className={ `flex min-w-0 flex-1 flex-wrap items-center gap-2` }>
          <Segment>
            <ToolbarMenu
              title={ `Text style` }
              buttonClassName={ textButton }
              options={ textStyleOptions }
              button={ (
                <>
                  <EditorIcon name={ `textStyle` }
                    className={ `text-gray-500` } />
                  { activeHeading ? `Heading ${activeHeading}` : `Body text` }
                  <EditorIcon name={ `chevronDown` }
                    size={ 12 }
                    className={ `text-gray-400` } />
                </>
              ) } />
            <ToolbarMenu
              title={ `Font` }
              buttonClassName={ textButton }
              options={ fontOptions }
              button={ (
                <>
                  <span style={ { fontFamily: currentFont?.value } }>{ currentFont?.label ?? `Default font` }</span>
                  <EditorIcon name={ `chevronDown` }
                    size={ 12 }
                    className={ `text-gray-400` } />
                </>
              ) } />
          </Segment>

          <Segment>
            { alignments.map(a => (
              <IconButton
                key={ a.value }
                title={ a.label }
                active={ editor.isActive({ textAlign: a.value }) }
                onClick={ () => editor.chain().focus().setTextAlign(a.value).run() }>
                <EditorIcon name={ a.icon } />
              </IconButton>
            )) }
          </Segment>

          <Segment>
            { linkOptions.length ? (
              <ToolbarMenu
                title={ editor.state.selection.empty && !editor.isActive(`link`) ? `Select text to add a link` : `Link` }
                disabled={ editor.state.selection.empty && !editor.isActive(`link`) }
                buttonClassName={ `flex h-7 w-7 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900` }
                options={ linkOptions }
                button={ <EditorIcon name={ `link` } /> } />
            ) : null }
            { mode === `email` && linkTags.length ? (
              <IconButton
                title={ `Button` }
                onClick={ () => insertButton(editor, `{{{${linkTags[0].value}}}}`) }>
                <EditorIcon name={ `button` } />
              </IconButton>
            ) : null }
            <IconButton
              title={ `Divider` }
              onClick={ () => editor.chain().focus().setHorizontalRule().run() }>
              <EditorIcon name={ `divider` } />
            </IconButton>
            <IconButton
              title={ `Line break` }
              onClick={ () => editor.chain().focus().setHardBreak().run() }>
              <EditorIcon name={ `lineBreak` } />
            </IconButton>
            { isSignature ? (
              <IconButton
                title={ `Image` }
                onClick={ () => {
                  const url = window.prompt(`Paste image url`);

                  if (url) editor.chain().focus().setImage({ src: url }).run();
                } }>
                <EditorIcon name={ `image` } />
              </IconButton>
            ) : null }
            <IconButton
              title={ `Show line breaks` }
              active={ editor.storage.invisibleCharacters?.visibility() }
              className={ `text-[15px]` }
              onClick={ () => editor.chain().focus().toggleInvisibleCharacters().run() }>
              { `¶` }
            </IconButton>
          </Segment>

          { extra ? <Segment>{ extra }</Segment> : null }
        </div>

        { fieldOptions.length || action ? (
          // A grid so the stacked buttons share a width
          <div className={ `grid shrink-0 gap-2` }>
            { fieldOptions.length ? <Segment className={ `[&>*]:flex-1` }>
              <ToolbarMenu
                align={ `right` }
                buttonClassName={ classNames(sideButton, `w-full`) }
                options={ fieldOptions }
                button={ (
                  <>
                    <EditorIcon name={ `field` }
                      size={ 14 } />
                    { `Insert field` }
                    <EditorIcon name={ `chevronDown` }
                      size={ 12 }
                      className={ `text-gray-400` } />
                  </>
                ) } />
            </Segment> : null }
            { action ? (
              <Segment>
                <button
                  type={ `button` }
                  disabled={ action.disabled }
                  onClick={ action.onClick }
                  className={ classNames(sideButton, `w-full disabled:cursor-not-allowed disabled:opacity-40`) }>
                  { action.icon }
                  { action.label }
                </button>
              </Segment>
            ) : null }
          </div>
        ) : null }
      </div>

      <BubbleMenu
        editor={ editor }
        shouldShow={ shouldShowBubbleMenu }
        options={ bubbleMenuOptions }
        className={ `flex items-center gap-0.5 whitespace-nowrap rounded-lg bg-paidniceGreen-800 p-1 shadow-[0_8px_20px_rgba(17,28,34,.25)] [&_button]:cursor-pointer` }>
        { button ? (
          <ButtonControls editor={ editor }
            button={ button }
            linkTags={ linkTags } />
        ) : (
          <>
            <button
              type={ `button` }
              title={ `Bold` }
              aria-label={ `Bold` }
              aria-pressed={ editor.isActive(`bold`) }
              className={ classNames(bubbleButton, editor.isActive(`bold`) && `bg-paidniceGreen-600 text-white`) }
              onClick={ () => editor.chain().focus().toggleBold().run() }>
              <EditorIcon name={ `bold` } />
            </button>
            <button
              type={ `button` }
              title={ `Italic` }
              aria-label={ `Italic` }
              aria-pressed={ editor.isActive(`italic`) }
              className={ classNames(bubbleButton, editor.isActive(`italic`) && `bg-paidniceGreen-600 text-white`) }
              onClick={ () => editor.chain().focus().toggleItalic().run() }>
              <EditorIcon name={ `italic` } />
            </button>
            <button
              type={ `button` }
              title={ `Underline` }
              aria-label={ `Underline` }
              aria-pressed={ editor.isActive(`underline`) }
              className={ classNames(bubbleButton, editor.isActive(`underline`) && `bg-paidniceGreen-600 text-white`) }
              onClick={ () => editor.chain().focus().toggleUnderline().run() }>
              <EditorIcon name={ `underline` } />
            </button>
            <ColorPickerEditor editor={ editor } />
            <ToolbarMenu
              title={ `Text size` }
              buttonClassName={ `flex h-7 items-center gap-1 rounded-[5px] px-1.5 text-xs text-paidniceGreen-100 hover:bg-paidniceGreen-700` }
              options={ fontSizeOptions }
              button={ (
                <>
                  { currentFontSize ? currentFontSize.replace(`px`, ``) : `Size` }
                  <EditorIcon name={ `chevronDown` }
                    size={ 12 } />
                </>
              ) } />

            { linkOptions.length ? (
              <>
                <span className={ `mx-1 h-[18px] w-px bg-paidniceGreen-600` } />
                { activeLinkLabel ? (
                  <>
                    <span className={ `flex h-7 items-center gap-1.5 rounded-[5px] bg-paidniceGreen-700 px-2 text-xs text-white` }>
                      <EditorIcon name={ `link` }
                        size={ 13 } />
                      { activeLinkLabel }
                    </span>
                    <ToolbarMenu
                      buttonClassName={ bubbleTextButton }
                      options={ linkOptions }
                      button={ `Edit` } />
                    <button
                      type={ `button` }
                      className={ bubbleTextButton }
                      onClick={ () => editor.chain().focus().extendMarkRange(`link`).unsetLink().run() }>
                      { `Remove` }
                    </button>
                  </>
                ) : (
                  <ToolbarMenu
                    title={ `Link` }
                    buttonClassName={ bubbleButton }
                    options={ linkOptions }
                    button={ <EditorIcon name={ `link` } /> } />
                ) }
              </>
            ) : null }
          </>
        ) }
      </BubbleMenu>
    </>
  );
}
